import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getSavedResults, computeWod } from "./compute";
import { ScreenType, Wod, ScreenData, Heat, WodDescription, Ranking } from "./types";
import Screen from "./screen";
import {getWorhouts} from "./workouts";
import {getHeats} from "./heats";
import {computeOverall, computeFinal} from "./overall";

// Type guard to check if data is a Wod
const isWod = (data: ScreenData): data is Wod => {
    return (data as Wod).results !== undefined && (data as Wod).type !== undefined;
};

// Type guard to check if data is a Heat
const isHeat = (data: ScreenData): data is Heat => {
    return (data as Heat).teams !== undefined;
};

// Type guard to check if data is a Ranking
const isRanking = (data: ScreenData): data is Ranking => {
    return (data as Ranking).result !== undefined && Array.isArray((data as Ranking).result);
};

// Helper to get screen name
const getScreenName = (data: ScreenData): string => {
    if (isWod(data)) return data.name;
    if (isHeat(data)) return `${data.name} - Heat ${data.number}`;
    if (isRanking(data)) return data.name;
    // WodDescription
    return (data as WodDescription).title;
};

// Helper to get screen category
const getScreenCategory = (data: ScreenData): string => {
    const category = data.category;
    return category === 0 ? 'SCALE' : 'RX';
};

// Helper to get team count
const getTeamCount = (data: ScreenData): number | null => {
    if (isWod(data)) return data.results.length;
    if (isRanking(data)) return data.result.length;
    return null;
};

// Robust equality for screens to match saved displayedScreens across types
const screenEquals = (a: ScreenData, b: ScreenData): boolean => {
    const normalize = (s?: any) => (s === undefined || s === null) ? '' : String(s).trim().toLowerCase();
    if (isWod(a) && isWod(b)) return normalize(a.name) === normalize(b.name);
    if (isHeat(a) && isHeat(b)) return normalize(a.name) === normalize(b.name) && Number((a as any).number) === Number((b as any).number);
    if (isRanking(a) && isRanking(b)) return normalize(a.name) === normalize(b.name);
    // WodDescription
    if (!isWod(a) && !isHeat(a) && !isRanking(a) && !isWod(b) && !isHeat(b) && !isRanking(b)) return normalize((a as WodDescription).title) === normalize((b as WodDescription).title);
    return false;
};

const ScreensManagement: React.FC = () => {
    const [screens, setScreens] = useState<ScreenType[]>([]);
    const [expandedScreens, setExpandedScreens] = useState<Set<number>>(new Set());
    // active tab state: 'WOD' | 'DESC' | 'HEAT' | 'RANKING'
    const [activeTab, setActiveTab] = useState<'WOD' | 'DESC' | 'HEAT' | 'RANKING'>('WOD');

    useEffect(() => {
        loadScreens();
    }, []);

    // Tab button subcomponent
    const TabButton: React.FC<{ label: string; id: 'WOD' | 'DESC' | 'HEAT' | 'RANKING' }> = ({ label, id }) => {
        const isActive = activeTab === id;
        // Active: vivid blue gradient for prominence; Inactive: light gray with dark text for contrast
        const activeClasses = 'px-4 py-2 rounded-md font-semibold bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-lg border border-blue-700';
        const inactiveClasses = 'px-4 py-2 rounded-md font-semibold bg-gray-200 text-gray-800 hover:bg-gray-300 border border-gray-300';
        return (
            <button
                onClick={() => setActiveTab(id)}
                className={isActive ? activeClasses : inactiveClasses}
            >
                {label}
            </button>
        );
    };

    const loadScreens = () => {
        const savedWods = getSavedResults();
        const workouts = getWorhouts();
        const heats = getHeats();
        const [scaleRanking, rxRanking] = computeOverall();
        const screens: ScreenType[] = [];

        // Load displayed screens from localStorage
        const displayedScreensKey = 'displayedScreens';
        const currentDisplayed = localStorage.getItem(displayedScreensKey);
        const displayedScreens: ScreenType[] = currentDisplayed ? JSON.parse(currentDisplayed) : [];

         for (const key in savedWods) {
             const wod = savedWods[key];
             const computedWod = computeWod(wod);
             screens.push({
                 data: computedWod,
                // Restore visibility by matching the saved screens robustly
                 visible: displayedScreens.some(ds => screenEquals(ds.data, computedWod))
             });
         }

         for (const wi in workouts){
             const wk = workouts[wi];
             screens.push({
                 data: wk,
                visible: displayedScreens.some(ds => screenEquals(ds.data, wk))
             });
         }
         for (const hi in heats){
             const ht = heats[hi];
             screens.push({
                 data: ht,
                visible: displayedScreens.some(ds => screenEquals(ds.data, ht))
             });
         }

         // Add Rankings
         if (scaleRanking.result.length > 0) {
             screens.push({
                 data: scaleRanking,
                 visible: displayedScreens.some(ds => screenEquals(ds.data, scaleRanking))
             });
         }
         if (rxRanking.result.length > 0) {
             screens.push({
                 data: rxRanking,
                 visible: displayedScreens.some(ds => screenEquals(ds.data, rxRanking))
             });
         }

         setScreens(screens);
     };

    const handleGenerateFinals = () => {
        try {
            const [scaleFinal, rxFinal] = computeFinal();

            // Find existing final WODs
            const scaleIndex = screens.findIndex(s => isWod(s.data) && s.data.name === scaleFinal.name && s.data.category === scaleFinal.category);
            const rxIndex = screens.findIndex(s => isWod(s.data) && s.data.name === rxFinal.name && s.data.category === rxFinal.category);

            const newScreens = [...screens];
            let scaleAction = '';
            let rxAction = '';

            // Replace or add Scale Final
            if (scaleIndex !== -1) {
                // Preserve visibility state when replacing
                newScreens[scaleIndex] = {
                    data: scaleFinal,
                    visible: newScreens[scaleIndex].visible
                };
                scaleAction = 'updated';
            } else {
                newScreens.push({
                    data: scaleFinal,
                    visible: false
                });
                scaleAction = 'added';
            }

            // Replace or add RX Final
            if (rxIndex !== -1) {
                // Preserve visibility state when replacing
                newScreens[rxIndex] = {
                    data: rxFinal,
                    visible: newScreens[rxIndex].visible
                };
                rxAction = 'updated';
            } else {
                newScreens.push({
                    data: rxFinal,
                    visible: false
                });
                rxAction = 'added';
            }

            setScreens(newScreens);

            // Show appropriate message based on actions taken
            if (scaleAction === 'updated' && rxAction === 'updated') {
                alert('Final events updated successfully! Both Scale and RX finals have been refreshed with the latest top 5 teams.');
            } else if (scaleAction === 'updated' || rxAction === 'updated') {
                alert(`Final events ${scaleAction === 'updated' ? 'Scale updated, RX added' : 'RX updated, Scale added'} successfully!`);
            } else {
                alert('Final events generated successfully! Both Scale and RX final WODs have been added to the RESULTS tab.');
            }

            // Switch to WOD tab to show the finals
            setActiveTab('WOD');

            console.log("Generated final events:", scaleFinal, rxFinal);
        } catch (error) {
            console.error("Error generating final events:", error);
            alert("Error generating final events. Please make sure you have saved WOD results first.");
        }
    };

    const toggleVisibility = (index: number) => {
         setScreens(prevScreens => {
             const updatedScreens = prevScreens.map((screen, i) =>
                 i === index ? { ...screen, visible: !screen.visible } : screen
             );

             // Save or remove from localStorage
             const selectedScreen = updatedScreens[index];
             const displayedScreensKey = 'displayedScreens';
             const currentDisplayed = localStorage.getItem(displayedScreensKey);
             let displayedScreens: ScreenType[] = currentDisplayed ? JSON.parse(currentDisplayed) : [];

             if (selectedScreen.visible) {
                 // Add to displayed screens if not already there
                const exists = displayedScreens.some(s => screenEquals(s.data, selectedScreen.data));
                if (!exists) displayedScreens.push(selectedScreen);
             } else {
                 // Remove from displayed screens
                displayedScreens = displayedScreens.filter(s => !screenEquals(s.data, selectedScreen.data));
             }

             localStorage.setItem(displayedScreensKey, JSON.stringify(displayedScreens));

             return updatedScreens;
         });
     };

    const toggleExpanded = (index: number) => {
        setExpandedScreens(prev => {
            const newSet = new Set(prev);
            if (newSet.has(index)) {
                newSet.delete(index);
            } else {
                newSet.add(index);
            }
            return newSet;
        });
    };

    // Precompute tab counts and filtered list to avoid inline IIFEs (and TypeScript implicit-any issues)
    const wodsCount = screens.filter(s => isWod(s.data)).length;
    const heatCount = screens.filter(s => isHeat(s.data)).length;
    const rankingCount = screens.filter(s => isRanking(s.data)).length;
    const descCount = screens.length - wodsCount - heatCount - rankingCount;

    const indexed = screens.map((s, i) => ({ s, i }));
    const filtered = indexed.filter(({ s }) => {
        if (activeTab === 'WOD') return isWod(s.data);
        if (activeTab === 'HEAT') return isHeat(s.data);
        if (activeTab === 'RANKING') return isRanking(s.data);
        return !isWod(s.data) && !isHeat(s.data) && !isRanking(s.data);
    });

    return (
        <div className="min-h-screen bg-gray-100 p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <h1 className="text-3xl font-bold text-gray-800">Screen Management</h1>
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={handleGenerateFinals}
                            className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-green-600 text-white font-bold rounded-lg shadow hover:from-emerald-700 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all hover:shadow-lg"
                        >
                            🏆 Generate Finals
                        </button>
                        <Link
                            to="/monitor"
                            target="_blank"
                            className="px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                        >
                            🖥️ Open Monitor
                        </Link>
                        {/* Clear displayed screens button */}
                        <button
                            onClick={() => {
                                // clear displayed screens from localStorage and update state
                                localStorage.removeItem('displayedScreens');
                                setScreens(prev => prev.map(s => ({ ...s, visible: false })));
                            }}
                            className="px-4 py-2 bg-red-500 text-white font-semibold rounded-lg shadow hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2 transition-colors"
                        >
                            Clear displayedScreens
                        </button>
                    </div>
                </div>

                {/* Tabs: WODs | Descriptions | HEATS | RANKINGS */}
                <div className="mb-6">
                    {/* dark panel behind tabs to improve contrast on the page's gray background */}
                    <div className="inline-flex items-center space-x-3  p-2 rounded-lg">
                        <TabButton label={`RESULTS (${wodsCount})`} id="WOD" />
                        <TabButton label={`WOD DESCRIPTIONS (${descCount})`} id="DESC" />
                        <TabButton label={`HEATS (${heatCount})`} id="HEAT" />
                        <TabButton label={`RANKINGS (${rankingCount})`} id="RANKING" />
                    </div>
                </div>

                {/* Tab content container */}
                <div className="space-y-6">
                    {/* Render filtered screens (preserve original indexes so toggle funcs still work) */}
                    {filtered.map(({ s: screen, i: index }) => {
                        const isExpanded = expandedScreens.has(index);
                        const headerStyles = isWod(screen.data)
                            ? {
                                bg: 'bg-gray-800 border-b border-gray-700 hover:bg-gray-700',
                                text: 'text-white',
                                icon: 'text-white'
                              }
                            : isHeat(screen.data)
                            ? {
                                bg: 'bg-amber-600 border-b border-amber-500 hover:bg-amber-500',
                                text: 'text-white',
                                icon: 'text-white'
                              }
                            : isRanking(screen.data)
                            ? {
                                bg: 'bg-purple-600 border-b border-purple-500 hover:bg-purple-500',
                                text: 'text-white',
                                icon: 'text-white'
                              }
                            : {
                                bg: 'bg-blue-600 border-b border-blue-500 hover:bg-blue-500',
                                text: 'text-white',
                                icon: 'text-white'
                              };
                        const headerBg = headerStyles.bg;
                        return (
                            <div key={index} className="bg-white rounded-lg shadow-lg overflow-hidden">
                                {/* Control Panel - Clickable Header */}
                                <div
                                    className={`${headerBg} p-4 cursor-pointer transition-colors`}
                                    onClick={() => toggleExpanded(index)}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            <svg
                                                className={`w-6 h-6 ${headerStyles.icon} transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`}
                                                fill="none"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path d="M9 5l7 7-7 7"></path>
                                            </svg>
                                            <div>
                                                <div className={`text-lg font-semibold ${headerStyles.text}`}>
                                                    {getScreenName(screen.data)}
                                                </div>
                                                <div className={`${headerStyles.text} text-sm opacity-80`}>
                                                    {getScreenCategory(screen.data)}{getTeamCount(screen.data) !== null ? ` - ${getTeamCount(screen.data)} teams` : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center space-x-4" onClick={(e) => e.stopPropagation()}>
                                            <label className="flex items-center space-x-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={screen.visible}
                                                    onChange={() => toggleVisibility(index)}
                                                    className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                                <span className={`text-sm font-semibold ${headerStyles.text}`}>
                                                    Display on Monitor
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                {/* Preview - Collapsible */}
                                {isExpanded && (
                                    <div className="border-t border-gray-200">
                                        <div className="transform scale-50 origin-top-left" style={{ width: '200%', height: 'auto' }}>
                                            <Screen screen={screen} />
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Refresh Button */}
                <div className="mt-8 flex justify-center">
                    <button
                        onClick={loadScreens}
                        className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                    >
                        Reload WODs
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ScreensManagement;
